import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailLayout } from '@/components/ProfileMenu/DetailLayout';
import { PasswordField } from '@/components/ProfileMenu/PasswordField';
import { PasswordRequirements } from '@/components/ProfileMenu/PasswordRequirements';
import { useUpdatePassword } from '@/components/ProfileMenu/hooks/useUpdatePassword';
import {
  ErrorMessage,
  getResponseErrorCode,
  logRequestError,
} from '@/utils/errors';
import { getPasswordCriteria, isValidPassword } from '@/utils/validation';

const CURRENT_PASSWORD_INVALID_CODE = 'CurrentPasswordInvalid';
const NEW_PASSWORD_INVALID_CODE = 'NewPasswordInvalid';

interface Props {
  onBack: () => void;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}

export const PasswordView: React.FC<Props> = ({
  onBack,
  onClose,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCurrentInvalid, setIsCurrentInvalid] = useState(false);
  const [isNewInvalid, setIsNewInvalid] = useState(false);
  const [isMismatch, setIsMismatch] = useState(false);

  const criteria = getPasswordCriteria(newPassword);
  const isSaveDisabled =
    currentPassword.length === 0 ||
    newPassword.length === 0 ||
    confirmPassword.length === 0;

  const { updatePassword, isPending } = useUpdatePassword();

  const handleCurrentChange = (value: string) => {
    setCurrentPassword(value);

    if (isCurrentInvalid) {
      setIsCurrentInvalid(false);
    }
  };

  const handleNewChange = (value: string) => {
    setNewPassword(value);

    if (isNewInvalid) {
      setIsNewInvalid(false);
    }

    if (isMismatch) {
      setIsMismatch(false);
    }
  };

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);

    if (isMismatch) {
      setIsMismatch(false);
    }
  };

  const handleSave = async () => {
    if (isPending || isSaveDisabled) {
      return;
    }

    const arePasswordsValid = isValidPassword(newPassword);
    const doPasswordsMatch = newPassword === confirmPassword;

    setIsNewInvalid(!arePasswordsValid);
    setIsMismatch(!doPasswordsMatch);

    if (!arePasswordsValid || !doPasswordsMatch) {
      return;
    }

    try {
      await updatePassword({
        currentPassword,
        newPassword,
        newPasswordConfirmation: confirmPassword,
      });
      onSuccess();
    } catch (requestError) {
      const code = getResponseErrorCode(requestError);

      if (code === CURRENT_PASSWORD_INVALID_CODE) {
        setIsCurrentInvalid(true);
        return;
      }

      if (code === NEW_PASSWORD_INVALID_CODE) {
        setIsNewInvalid(true);
        return;
      }

      logRequestError(ErrorMessage.ChangePassword, requestError);
      onError();
    }
  };

  return (
    <DetailLayout
      title={t('changePassword.title')}
      isSaveDisabled={isSaveDisabled}
      isSaving={isPending}
      onBack={onBack}
      onClose={onClose}
      onSave={handleSave}
    >
      <div className="mt-4 flex flex-col gap-5 md:mt-0">
        <PasswordField
          label={t('changePassword.currentLabel')}
          value={currentPassword}
          placeholder={t('changePassword.currentLabel')}
          error={
            isCurrentInvalid ? t('changePassword.currentWrong') : undefined
          }
          onChange={handleCurrentChange}
          onSubmit={handleSave}
        />
        <PasswordField
          label={t('changePassword.newLabel')}
          value={newPassword}
          placeholder={t('changePassword.newLabel')}
          error={
            isNewInvalid ? t('changePassword.requirementsNotMet') : undefined
          }
          onChange={handleNewChange}
          onSubmit={handleSave}
        />
        <PasswordField
          label={t('changePassword.confirmLabel')}
          value={confirmPassword}
          placeholder={t('changePassword.confirmLabel')}
          error={isMismatch ? t('changePassword.mismatch') : undefined}
          onChange={handleConfirmChange}
          onSubmit={handleSave}
        />
        <PasswordRequirements criteria={criteria} />
      </div>
    </DetailLayout>
  );
};
